import DataIssuesPanel from "@/components/DataIssuesPanel";
import PlantLinker from "@/components/PlantLinker";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { Upload, Download, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Eye, MailWarning } from "lucide-react";
import { unregisteredRows, unregisteredRowText } from "@/lib/registrationEmail";

export default function SupplierBulkUpload() {
  const location = useLocation();
  const isBuyerView = location.pathname.startsWith("/comprador/");
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("insert"); // insert · upsert · dry_run
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [plant, setPlant] = useState("");
  const [plantas, setPlantas] = useState([]);
  useEffect(() => { http.get("/plantas").then((r) => setPlantas(r.data.plantas || r.data || [])).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await http.get("/admin/suppliers/csv-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_proveedores.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr("No se pudo descargar la plantilla.");
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setErr(""); setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("update_existing", mode === "upsert" ? "true" : "false");
      form.append("dry_run", mode === "dry_run" ? "true" : "false");
      if (plant) form.append("plant", plant);
      const res = await http.post("/admin/suppliers/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const backTo = isBuyerView ? "/comprador" : "/proveedores";
  const backLabel = isBuyerView ? "Volver al comprador" : "Volver a Proveedores";
  const eyebrow = isBuyerView ? "mis proveedores" : "carga masiva";
  const title = isBuyerView ? "Cargar mis proveedores" : "Importar proveedores desde CSV";
  const lead = isBuyerView
    ? "¿Para qué sirve la plantilla? Es un archivo CSV con las columnas exactas que Lala Elite espera. La descargas, la llenas en Excel/Google Sheets con TUS proveedores asignados, la guardas como CSV y la subes acá. Nosotros te decimos qué se creó, qué se actualizó y qué se rechazó (con motivo). Puedes correr un simulacro (dry-run) antes de escribir en la base."
    : "Sube un archivo CSV para crear varios proveedores en un solo paso. RFCs y SAP IDs duplicados se omiten o actualizan según el modo. Los proveedores quedan automáticamente asignados al comprador (buyer_email) que indiques.";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="bulk-upload-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to={backTo} data-testid="back-suppliers"
            className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
            <ArrowLeft size={14} /> {backLabel}
          </Link>
          <div className="font-hand text-xl text-[var(--lala-blue)] mt-2">{eyebrow}</div>
          <h1 className="font-display text-4xl">{title}</h1>
          <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">{lead}</p>
        </div>
        <button onClick={downloadTemplate} data-testid="download-template-btn"
          className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[var(--cream-soft)]">
          <Download size={14} /> Descargar plantilla
        </button>
      </div>

      {!isBuyerView && <DataIssuesPanel />}

      <form onSubmit={upload} className="sketch-border bg-[var(--cream-soft)] p-6 grid gap-5" data-testid="bulk-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Archivo CSV</label>
          <input type="file" accept=".csv,text/csv" required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="bulk-file-input"
            className="block mt-1 text-sm" />
          <div className="text-xs text-[var(--ink-soft)] mt-1">
            Columnas: <code>name</code> (requerido), <code>contact_email</code>, <code>rfc</code>, <code>category</code>{" "}
            (catálogo oficial: Asépticos · Empaques · Ingredientes · Materia prima cárnica · Maquilas Co-Man · Maquilas Co-Pack), <code>contact_name</code>,{" "}
            <code>phone</code>, <code>sap_id</code> (ID LALA), <code>segment</code>, <code>buyer_name</code>{" "}
            (nombre del comprador Lala), <code>buyer_email</code>. Si el <code>buyer_email</code> corresponde a un usuario buyer existente, el proveedor queda auto-asignado a esa persona.
          </div>
        </div>

        <div data-testid="bulk-plant-select-wrap">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Planta (obligatoria)</label>
          <select value={plant} onChange={(e) => setPlant(e.target.value)} required
            className="block mt-1 border border-[var(--line)] rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="bulk-plant-select">
            <option value="">Selecciona una planta…</option>
            {plantas.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="text-xs text-[var(--ink-soft)] mt-1">
            Este flujo carga la base de proveedores de una planta específica: los proveedores creados o actualizados
            quedarán relacionados con ella (un proveedor puede pertenecer a varias plantas; no se duplica).
          </div>
        </div>

        <fieldset className="grid gap-2" data-testid="bulk-mode-radio">
          <legend className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Modo</legend>
          <ModeOption id="mode-insert" label="Solo insertar" description="Rechaza si el RFC o SAP ID ya existe."
            checked={mode === "insert"} onChange={() => setMode("insert")} value="insert" />
          <ModeOption id="mode-upsert" label="Insertar + actualizar (upsert)" description="Match por RFC o SAP ID; actualiza los campos enviados."
            checked={mode === "upsert"} onChange={() => setMode("upsert")} value="upsert" />
          <ModeOption id="mode-dryrun" label="Dry-run (simulacro sin escribir)" description="Muestra qué crearía/actualizaría/omitiría sin tocar la base."
            checked={mode === "dry_run"} onChange={() => setMode("dry_run")} value="dry_run" />
        </fieldset>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy || !file || !plant} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="bulk-submit">
            {mode === "dry_run" ? <Eye size={16} /> : <Upload size={16} />}
            {busy ? "Procesando…" : (mode === "dry_run" ? "Correr simulacro" : "Subir y procesar")}
          </button>
          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 text-sm" data-testid="bulk-error">{err}</div>}
        </div>
      </form>

      {result && (
        <div className="mt-8 space-y-4" data-testid="bulk-result">
          {result.summary?.dry_run && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 inline-flex items-center gap-2" data-testid="bulk-dry-badge">
              <Eye size={14} /> <strong>Dry-run:</strong> no se escribió nada. Los IDs y proveedores mostrados son una simulación.
            </div>
          )}
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card label="Creados" value={result.summary.created} tone="green" testid="card-created" />
            <Card label="Actualizados" value={result.summary.updated} tone="blue" testid="card-updated" />
            <Card label={`Relacionados${result.summary.plant ? ` · ${result.summary.plant}` : " a planta"}`} value={result.summary.linked_plant ?? 0} tone="blue" testid="card-linked-plant" />
            <Card label="Omitidos" value={result.summary.skipped} tone="amber" testid="card-skipped" />
            <Card label="Rechazados" value={result.summary.invalid} tone="red" testid="card-invalid" />
            {!result.summary.dry_run && (
              <Card label="Sin correo registrado" value={result.summary.emails_not_registered ?? 0}
                tone={result.summary.emails_not_registered ? "amber" : "blue"} testid="card-emails-not-registered" />
            )}
          </div>

          {!result.summary?.dry_run && unregisteredRows(result.created).length > 0 && (
            <Section title="Creados sin correo registrado" icon={<MailWarning size={16} className="text-amber-700" />}
              testid="section-email-not-registered">
              {unregisteredRows(result.created).map((r, i) => <Row key={i} text={unregisteredRowText(r)} />)}
            </Section>
          )}

          {result.invalid.length > 0 && (
            <Section title="Inválidos" icon={<XCircle size={16} className="text-red-700" />} testid="section-invalid">
              {result.invalid.map((r, i) => (
                <Row key={i} text={`Línea ${r.line}: ${r.name || "(sin nombre)"} — ${r.reason}`} />
              ))}
            </Section>
          )}
          {result.skipped.length > 0 && (
            <Section title="Omitidos" icon={<AlertTriangle size={16} className="text-amber-700" />} testid="section-skipped">
              {result.skipped.map((r, i) => (
                <Row key={i} text={`Línea ${r.line}: ${r.name} (RFC ${r.rfc || "—"}) — ${r.reason}`} />
              ))}
            </Section>
          )}
          {(result.created.length > 0 || result.updated.length > 0) && (
            <Section title={result.summary?.dry_run ? "Se crearían / actualizarían" : "Procesados"}
              icon={<CheckCircle2 size={16} className="text-green-700" />} testid="section-processed">
              {[...result.created, ...result.updated].map((r, i) => (
                <Row key={i} text={`Línea ${r.line}: ${r.name} ${r.rfc ? `(RFC ${r.rfc})` : ""} ${r.sap_id ? `· SAP ${r.sap_id}` : ""}`} />
              ))}
            </Section>
          )}
        </div>
      )}

      <PlantLinker plantas={plantas} />
    </div>
  );
}

function ModeOption({ id, label, description, checked, onChange, value }) {
  return (
    <label htmlFor={id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-[var(--lala-blue)] bg-white" : "border-[var(--line)] hover:bg-white"}`}
      data-testid={`bulk-mode-${value}`}>
      <input id={id} type="radio" name="bulk-mode" checked={checked} onChange={onChange}
        className="accent-[var(--lala-blue)] mt-1" />
      <div>
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-[var(--ink-soft)]">{description}</div>
      </div>
    </label>
  );
}

function Card({ label, value, tone, testid }) {
  const tones = {
    green: "bg-green-50 border-green-200 text-green-800",
    blue: "bg-[var(--cream-soft)] border-[var(--lala-blue)] text-[var(--lala-blue)]",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div data-testid={testid} className={`border rounded-xl p-4 ${tones[tone]}`}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-3xl">{value}</div>
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <div data-testid={testid} className="sketch-border-soft bg-[var(--cream)] p-4">
      <div className="flex items-center gap-2 mb-2">{icon}<div className="font-semibold">{title}</div></div>
      <ul className="text-sm space-y-1">{children}</ul>
    </div>
  );
}

function Row({ text }) {
  return <li className="text-[var(--ink)]">• {text}</li>;
}
